import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download, X, MessageSquare, FolderArchive, ChevronLeft, ChevronRight,
  Upload, Zap, Library, LogOut, UserPlus, Copy, Check
} from 'lucide-react';
import { uploadFiles, uploadBatch, downloadFile, deleteFile, getMyInviteCode } from '../api';

const SIDEBAR_EXPANDED = 224; // px
const SIDEBAR_COLLAPSED = 56; // px

export default function Sidebar({
  files,
  selectedFile,
  onSelectFile,
  onBackToChat,
  onUpload,
  onDelete,
  uploading,
  setUploading,
  viewMode,
  collapsed,
  onToggleCollapse,
  currentUser,
  onLogout,
}) {
  const [mdFile, setMdFile] = useState(null);
  const [origFile, setOrigFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const mdInputRef = useRef(null);
  const origInputRef = useRef(null);
  const zipInputRef = useRef(null);

  // Invite state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [myCode, setMyCode] = useState('');
  const [joinedUsers, setJoinedUsers] = useState([]);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);

  const maskPhone = (phone) => {
    if (!phone || phone.length < 7) return phone;
    return phone.slice(0, 3) + '****' + phone.slice(-4);
  };

  const loadInviteInfo = async () => {
    setInviteLoading(true);
    try {
      const res = await getMyInviteCode();
      setMyCode(res.data.code);
      setJoinedUsers(res.data.joined || []);
    } catch (e) {
      setMyCode(currentUser?.user?.invite_code || '');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleOpenInvite = () => {
    setInviteOpen(true);
    setInviteCopied(false);
    loadInviteInfo();
  };

  const handleCopyInvite = () => {
    navigator.clipboard.writeText(myCode).then(() => {
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 3000);
    });
  };

  const handleUpload = async () => {
    if (!origFile) return;
    setUploading(true);
    try {
      await uploadFiles(mdFile, origFile);
      setMdFile(null);
      setOrigFile(null);
      if (mdInputRef.current) mdInputRef.current.value = '';
      if (origInputRef.current) origInputRef.current.value = '';
      onUpload();
    } catch (e) {
      alert('上传失败: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const handleBatchUpload = async () => {
    if (!zipFile) return;
    setUploading(true);
    try {
      await uploadBatch(zipFile);
      setZipFile(null);
      if (zipInputRef.current) zipInputRef.current.value = '';
      onUpload();
    } catch (e) {
      alert('批量上传失败: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (e, file) => {
    e.stopPropagation();
    if (!window.confirm(`确定删除 "${file.base_name}" 吗？`)) return;
    try {
      await deleteFile(file.base_name);
      onDelete(file.base_name);
    } catch (err) {
      alert('删除失败');
    }
  };

  const handleDownload = (e, file) => {
    e.stopPropagation();
    downloadFile(file.base_name, file.original_filename);
  };

  const isExpanded = !collapsed;

  return (
    <>
      {/* ── Sidebar ── */}
      <motion.aside
        animate={{ width: isExpanded ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED }}
        transition={{ type: 'spring', stiffness: 400, damping: 32 }}
        className="relative flex flex-col h-full flex-shrink-0 overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.62) 100%)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          borderRight: '1px solid rgba(255,255,255,0.55)',
          boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.8), 4px 0 24px rgba(0,0,0,0.04)',
        }}
      >
        {/* Header */}
        <div
          className="h-12 flex items-center flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
        >
          {isExpanded ? (
            <motion.div
              initial={false}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center px-4 w-full"
            >
              <Library size={16} className="text-gray-700 mr-2.5 flex-shrink-0" />
              <span className="text-sm font-semibold text-gray-900 tracking-tight truncate">
                Argon 知识库
              </span>
            </motion.div>
          ) : (
            <div className="w-full flex justify-center">
              <Library size={18} className="text-gray-700" />
            </div>
          )}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-2">
          {/* Chat nav */}
          <div className={`mb-3 ${isExpanded ? 'px-3' : 'px-1.5'}`}>
            <button
              onClick={onBackToChat}
              className={`w-full flex items-center rounded-xl text-sm transition-all duration-200 ${
                isExpanded ? 'space-x-2.5 px-2.5 py-1.5' : 'justify-center py-1.5'
              } ${
                viewMode === 'chat'
                  ? 'bg-white/60 text-gray-900 font-medium shadow-sm'
                  : 'text-gray-600 hover:bg-white/40'
              }`}
              style={{
                backdropFilter: viewMode === 'chat' ? 'blur(8px)' : 'none',
                border: viewMode === 'chat' ? '1px solid rgba(255,255,255,0.7)' : '1px solid transparent',
              }}
              title={!isExpanded ? 'AI 问答' : undefined}
            >
              <MessageSquare size={15} className="flex-shrink-0" />
              <AnimatePresence>
                {isExpanded && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.15 }}
                    className="whitespace-nowrap overflow-hidden"
                  >
                    AI 问答
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>

          {/* Section label */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="px-5 mb-1.5"
              >
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  文档
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* File list */}
          <div className={`space-y-0.5 ${isExpanded ? 'px-2' : 'px-1'}`}>
            {files.length === 0 && isExpanded && (
              <p className="text-xs text-gray-400 px-3 py-3">暂无文档，请上传</p>
            )}
            {files.map((file) => (
              <div
                key={file.base_name}
                onClick={() => onSelectFile(file)}
                className={`group flex items-center rounded-xl cursor-pointer transition-all duration-150 ${
                  isExpanded ? 'px-2.5 py-1.5' : 'px-1 py-1.5 justify-center'
                } ${
                  selectedFile?.base_name === file.base_name && viewMode === 'preview'
                    ? 'bg-white/70 text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:bg-white/40'
                }`}
                style={{
                  border: selectedFile?.base_name === file.base_name && viewMode === 'preview'
                    ? '1px solid rgba(255,255,255,0.8)'
                    : '1px solid transparent',
                }}
                title={!isExpanded ? file.base_name : undefined}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0"
                  style={{
                    background: selectedFile?.base_name === file.base_name && viewMode === 'preview'
                      ? 'rgba(59,130,246,0.8)'
                      : undefined,
                    boxShadow: selectedFile?.base_name === file.base_name && viewMode === 'preview'
                      ? '0 0 6px rgba(59,130,246,0.4)'
                      : undefined,
                  }}
                />
                <AnimatePresence>
                  {isExpanded && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.12 }}
                      className="text-[13px] truncate flex-1 leading-snug ml-2.5"
                    >
                      {file.base_name}
                    </motion.span>
                  )}
                </AnimatePresence>
                {isExpanded && !file.has_markdown && (
                  <span className="text-[9px] bg-amber-50/80 text-amber-600 px-1 rounded mr-1 flex-shrink-0 backdrop-blur-sm">
                    无md
                  </span>
                )}
                {isExpanded && (
                  <div className="flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-1">
                    <button
                      onClick={(e) => handleDownload(e, file)}
                      className="p-1 hover:bg-white/60 rounded transition-colors text-gray-500"
                      title="下载"
                    >
                      <Download size={12} />
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, file)}
                      className="p-1 hover:bg-white/60 rounded transition-colors text-gray-400 hover:text-red-500"
                      title="删除"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Upload section */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="flex-shrink-0 overflow-hidden"
              style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
            >
              <div className="p-3 space-y-3">
                {/* Hidden file inputs */}
                <input type="file" accept=".md" ref={mdInputRef} onChange={(e) => setMdFile(e.target.files?.[0] || null)} className="hidden" />
                <input type="file" accept=".pdf,.mp4,.mov,.avi,.mkv" ref={origInputRef} onChange={(e) => setOrigFile(e.target.files?.[0] || null)} className="hidden" />
                <input type="file" accept=".zip" ref={zipInputRef} onChange={(e) => setZipFile(e.target.files?.[0] || null)} className="hidden" />

                {/* Glass upload card */}
                <div
                  className="rounded-xl p-3 space-y-2"
                  style={{
                    background: 'rgba(255,255,255,0.45)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.6)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Upload size={12} className="text-gray-400" />
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">单文件上传</span>
                  </div>
                  <button
                    onClick={() => origInputRef.current?.click()}
                    className={`w-full text-left text-xs px-2.5 py-1.5 rounded-lg border border-dashed transition-all ${
                      origFile
                        ? 'border-green-300/70 bg-green-50/60 text-green-700'
                        : 'border-gray-300/60 text-gray-500 hover:border-gray-400 hover:bg-white/40'
                    }`}
                    style={{ backdropFilter: 'blur(4px)' }}
                  >
                    {origFile ? `✓ ${origFile.name}` : '选择 PDF / 视频 *'}
                  </button>
                  <button
                    onClick={() => mdInputRef.current?.click()}
                    className={`w-full text-left text-xs px-2.5 py-1.5 rounded-lg border border-dashed transition-all ${
                      mdFile
                        ? 'border-green-300/70 bg-green-50/60 text-green-700'
                        : 'border-gray-300/60 text-gray-500 hover:border-gray-400 hover:bg-white/40'
                    }`}
                    style={{ backdropFilter: 'blur(4px)' }}
                  >
                    {mdFile ? `✓ ${mdFile.name}` : '选择 .md 文件（可选）'}
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={!origFile || uploading}
                    className={`w-full text-xs py-1.5 rounded-lg font-medium transition-all ${
                      origFile && !uploading
                        ? 'bg-gray-900/90 text-white hover:bg-gray-800 shadow-sm'
                        : 'bg-gray-100/70 text-gray-400 cursor-not-allowed'
                    }`}
                    style={{ backdropFilter: 'blur(4px)' }}
                  >
                    {uploading ? '上传中...' : '上传'}
                  </button>
                  {origFile && origFile.name.endsWith('.pdf') && !mdFile && (
                    <p className="text-[10px] text-amber-600/90">未选择 .md，将自动提取 PDF 文本</p>
                  )}
                </div>

                {/* Glass batch upload card */}
                <div
                  className="rounded-xl p-3 space-y-2"
                  style={{
                    background: 'rgba(255,255,255,0.45)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.6)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Zap size={12} className="text-gray-400" />
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">批量上传</span>
                  </div>
                  <button
                    onClick={() => zipInputRef.current?.click()}
                    className={`w-full flex items-center space-x-1.5 text-left text-xs px-2.5 py-1.5 rounded-lg border border-dashed transition-all ${
                      zipFile
                        ? 'border-green-300/70 bg-green-50/60 text-green-700'
                        : 'border-gray-300/60 text-gray-500 hover:border-gray-400 hover:bg-white/40'
                    }`}
                    style={{ backdropFilter: 'blur(4px)' }}
                  >
                    <FolderArchive size={14} />
                    <span>{zipFile ? `✓ ${zipFile.name}` : '选择文件夹 Zip'}</span>
                  </button>
                  <button
                    onClick={handleBatchUpload}
                    disabled={!zipFile || uploading}
                    className={`w-full text-xs py-1.5 rounded-lg font-medium transition-all ${
                      zipFile && !uploading
                        ? 'bg-gray-900/90 text-white hover:bg-gray-800 shadow-sm'
                        : 'bg-gray-100/70 text-gray-400 cursor-not-allowed'
                    }`}
                    style={{ backdropFilter: 'blur(4px)' }}
                  >
                    {uploading ? '上传中...' : '批量上传'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* User info section */}
        <div
          className="flex-shrink-0"
          style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
        >
          {isExpanded ? (
            <div className="p-3 space-y-2">
              {/* User phone */}
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-[10px] font-semibold text-gray-600">
                    {currentUser?.user?.phone?.slice(-2) || '?'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 truncate">
                    {currentUser?.user?.phone ? maskPhone(currentUser.user.phone) : ''}
                  </p>
                  <p className="text-[10px] text-gray-400 truncate">
                    {currentUser?.kb?.name || '知识库'}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={handleOpenInvite}
                  className="flex-1 flex items-center justify-center space-x-1 text-xs py-1.5 rounded-lg
                    text-gray-600 hover:bg-white/40 transition-colors"
                  title="邀请成员"
                >
                  <UserPlus size={12} />
                  <span>邀请成员</span>
                </button>
                <button
                  onClick={onLogout}
                  className="flex items-center justify-center space-x-1 text-xs py-1.5 px-2 rounded-lg
                    text-gray-400 hover:text-red-500 hover:bg-white/40 transition-colors"
                  title="退出登录"
                >
                  <LogOut size={12} />
                </button>
              </div>
            </div>
          ) : (
            <div className="p-2 flex flex-col items-center space-y-2">
              <button
                onClick={onLogout}
                className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-white/40 transition-colors"
                title="退出登录"
              >
                <LogOut size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Invite code modal */}
        <AnimatePresence>
          {inviteOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-40 flex items-center justify-center"
              style={{
                background: 'rgba(0,0,0,0.15)',
                backdropFilter: 'blur(4px)',
              }}
              onClick={() => setInviteOpen(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                onClick={(e) => e.stopPropagation()}
                className="w-72 p-4 rounded-xl mx-4"
                style={{
                  background: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255,255,255,0.7)',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
                }}
              >
                <p className="text-xs font-semibold text-gray-700 mb-2">我的邀请码</p>
                <p className="text-[10px] text-gray-400 mb-3">
                  将此邀请码分享给团队成员，对方在注册或加入时输入即可共享知识库
                </p>
                {inviteLoading ? (
                  <div className="text-xs text-gray-400 text-center py-3">加载中...</div>
                ) : myCode ? (
                  <>
                    <div className="flex items-center space-x-2 mb-3">
                      <code className="flex-1 text-sm font-mono bg-gray-100 rounded-lg px-3 py-2 text-center text-gray-900 tracking-wider select-all">
                        {myCode}
                      </code>
                      <button
                        onClick={handleCopyInvite}
                        className={`p-2 rounded-lg transition-colors ${
                          inviteCopied
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {inviteCopied ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                    {joinedUsers.length > 0 && (
                      <div className="mb-3">
                        <p className="text-[10px] font-medium text-gray-400 mb-1.5">
                          已通过此码加入 ({joinedUsers.length})
                        </p>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {joinedUsers.map((j, i) => (
                            <div key={i} className="flex items-center justify-between text-[10px] text-gray-500 px-2 py-1 bg-gray-50 rounded">
                              <span>{j.joined_phone ? maskPhone(j.joined_phone) : '未知'}</span>
                              <span className="text-gray-400">{j.joined_at?.slice(0, 10)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-xs text-gray-400 text-center py-3">未知错误</div>
                )}
                <button
                  onClick={() => setInviteOpen(false)}
                  className="w-full text-xs py-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                >
                  关闭
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Collapsed upload hint */}
        {!isExpanded && (
          <div className="flex-shrink-0 p-2 flex justify-center">
            <button
              onClick={onToggleCollapse}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/40 transition-all"
              title="展开以使用上传功能"
            >
              <Upload size={16} />
            </button>
          </div>
        )}
      </motion.aside>

      {/* ── Toggle button (floating on the edge) ── */}
      <motion.button
        onClick={onToggleCollapse}
        className="absolute z-30 flex items-center justify-center rounded-full cursor-pointer"
        style={{
          left: isExpanded ? SIDEBAR_EXPANDED - 14 : SIDEBAR_COLLAPSED - 14,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 28,
          height: 28,
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.7)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
        title={isExpanded ? '收起侧边栏' : '展开侧边栏'}
      >
        {isExpanded ? (
          <ChevronLeft size={14} className="text-gray-500" />
        ) : (
          <ChevronRight size={14} className="text-gray-500" />
        )}
      </motion.button>
    </>
  );
}
